import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { Icono } from './Icono';
import type { NombreIcono } from './iconos';
import './deslizador.css';

/**
 * RV Deslizador de rango (componentes.md, tabla de microinteracciones y §5g): «Desde **21:00** ······ Hasta **03:00**»
 * sobre una barra con dos bolas y marcas debajo. Valores en minutos desde las 00:00 del día; pasada la medianoche (1440)
 * la hora lleva «+1» con luna, también en «Desde» (G247). Arrastrar mueve la bola más cercana (pointer capture, dedo en
 * iPad); cada bola es `role="slider"` con ←→, RePág/AvPág (4 pasos), Inicio y Fin.
 *
 * Con `escribirDesde` / `escribirHasta`, las dos horas grandes son **campos** (teclado numérico): se aplica al completar
 * 4 cifras, al salir o con Intro. La pantalla interpreta lo escrito (puede dar una duración que el deslizador no alcanza,
 * hasta 24 h) y devuelve el error que hay que pintar o ''. Sin ellas, horas de solo lectura.
 */
export interface MarcaDeslizador {
    valor: number;
    texto: string;
    icono?: NombreIcono;
}

interface Props {
    /** Para lectores de pantalla: «Horario de lunes a viernes, tramo 1». */
    etiqueta: string;
    desde: number;
    hasta: number;
    min: number;
    max: number;
    paso: number;
    /** Distancia mínima y máxima entre las bolas. */
    minDist?: number;
    maxDist?: number;
    marcas?: readonly MarcaDeslizador[];
    desactivado?: boolean;
    error?: string;
    onRango: (desde: number, hasta: number) => void;
    escribirDesde?: (texto: string) => string;
    escribirHasta?: (texto: string) => string;
    /** Si la hora completa es de otra forma que el campo («21:30»), la pantalla la escribe: por defecto, HH:MM. */
    formato?: (minutos: number) => string;
}

const DIA = 1440;
const hora = (m: number) => {
    const x = ((m % DIA) + DIA) % DIA;
    return `${String(Math.floor(x / 60)).padStart(2, '0')}:${String(x % 60).padStart(2, '0')}`;
};
const completa = (t: string) => /^\d{4}$/.test(t.trim()) || /^\d{1,2}[:.h]\d{2}$/.test(t.trim());

function CampoHora({ valor, etiqueta, desactivado, escribir, error }: { valor: string; etiqueta: string; desactivado: boolean; escribir: ((t: string) => string) | undefined; error: string }) {
    // Lo escrito se queda en el campo mientras no se aplica (o si da error); aplicado, el campo vuelve a enseñar el valor.
    const [texto, setTexto] = useState<string | null>(null);
    const aplicar = (t: string) => {
        if (escribir === undefined) {
            return;
        }
        if (t.trim() === '') {
            setTexto(null);
            return;
        }
        setTexto(escribir(t) === '' ? null : t);
    };
    if (escribir === undefined || desactivado) {
        return <span className="rv-desl__hora">{valor}</span>;
    }
    return (
        <input
            className={`rv-desl__campo${error !== '' ? ' rv-desl__campo--error' : ''}`}
            aria-label={etiqueta}
            aria-invalid={error !== ''}
            inputMode="numeric"
            enterKeyHint="done"
            maxLength={5}
            value={texto ?? valor}
            onFocus={(e) => {
                e.currentTarget.select();
            }}
            onChange={(e) => {
                const t = e.target.value;
                setTexto(t);
                if (completa(t)) {
                    aplicar(t);
                }
            }}
            onBlur={() => {
                if (texto !== null) {
                    aplicar(texto);
                }
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter' && texto !== null) {
                    e.preventDefault();
                    aplicar(texto);
                }
            }}
        />
    );
}

function MasUno() {
    return (
        <span className="rv-desl__mas" aria-label="del día siguiente">
            <Icono nombre="moon" tamano={12} />
            <span aria-hidden="true">+1</span>
        </span>
    );
}

export function Deslizador({ etiqueta, desde, hasta, min, max, paso, minDist = paso, maxDist = DIA, marcas = [], desactivado = false, error = '', onRango, escribirDesde, escribirHasta, formato = hora }: Props) {
    const barra = useRef<HTMLDivElement>(null);
    const arrastrando = useRef<'desde' | 'hasta' | null>(null);
    const [erroresCampo, setErroresCampo] = useState({ desde: '', hasta: '' });
    const ultimo = useRef({ desde, hasta });
    useEffect(() => {
        ultimo.current = { desde, hasta };
    });

    const pos = (v: number) => `${String(((Math.min(Math.max(v, min), max) - min) / (max - min)) * 100)}%`;
    const fijar = (cual: 'desde' | 'hasta', v: number) => {
        const { desde: d, hasta: h } = ultimo.current;
        setErroresCampo({ desde: '', hasta: '' });
        const redondo = Math.round(v / paso) * paso;
        if (cual === 'desde') {
            const nd = Math.min(Math.max(redondo, min), Math.min(max - minDist, h - minDist));
            onRango(nd, Math.min(Math.max(h, nd + minDist), nd + maxDist));
        } else {
            const nh = Math.max(Math.min(redondo, max, d + maxDist), d + minDist);
            onRango(d, nh);
        }
    };
    const valorEn = (x: number) => {
        const caja = barra.current?.getBoundingClientRect();
        if (caja === undefined || caja.width === 0) {
            return null;
        }
        return min + ((x - caja.left) / caja.width) * (max - min);
    };
    const bajar = (e: PointerEvent<HTMLDivElement>) => {
        if (desactivado) {
            return;
        }
        const v = valorEn(e.clientX);
        if (v === null) {
            return;
        }
        const cual = Math.abs(v - desde) <= Math.abs(v - hasta) ? 'desde' : 'hasta';
        arrastrando.current = cual;
        e.currentTarget.setPointerCapture(e.pointerId);
        fijar(cual, v);
    };
    const mover = (e: PointerEvent<HTMLDivElement>) => {
        if (arrastrando.current === null) {
            return;
        }
        const v = valorEn(e.clientX);
        if (v !== null) {
            fijar(arrastrando.current, v);
        }
    };
    const soltar = () => {
        arrastrando.current = null;
    };
    const tecla = (cual: 'desde' | 'hasta') => (e: KeyboardEvent<HTMLSpanElement>) => {
        if (desactivado) {
            return;
        }
        const actual = cual === 'desde' ? desde : hasta;
        const salto: Record<string, number> = { ArrowLeft: -paso, ArrowDown: -paso, ArrowRight: paso, ArrowUp: paso, PageDown: -4 * paso, PageUp: 4 * paso };
        let v: number | null = null;
        if (e.key in salto) {
            v = actual + (salto[e.key] ?? 0);
        } else if (e.key === 'Home') {
            v = min;
        } else if (e.key === 'End') {
            v = max;
        }
        if (v !== null) {
            e.preventDefault();
            fijar(cual, v);
        }
    };
    const conCampo = (cual: 'desde' | 'hasta', fn: ((t: string) => string) | undefined) =>
        fn === undefined
            ? undefined
            : (t: string) => {
                  const e = fn(t);
                  setErroresCampo((x) => ({ ...x, [cual]: e }));
                  return e;
              };
    const errorCampo = erroresCampo.desde !== '' ? erroresCampo.desde : erroresCampo.hasta;
    const errorVisible = errorCampo !== '' ? errorCampo : error;
    const bola = (cual: 'desde' | 'hasta', v: number) => (
        <span
            role="slider"
            tabIndex={desactivado ? -1 : 0}
            aria-label={`${cual === 'desde' ? 'Desde' : 'Hasta'} · ${etiqueta}`}
            aria-valuemin={cual === 'desde' ? min : desde + minDist}
            aria-valuemax={cual === 'desde' ? hasta - minDist : Math.min(max, desde + maxDist)}
            aria-valuenow={v}
            aria-valuetext={`${formato(v)}${v > DIA || (cual === 'desde' && v >= DIA) ? ' del día siguiente' : ''}`}
            aria-disabled={desactivado}
            className="rv-desl__bola"
            style={{ left: pos(v) }}
            onKeyDown={tecla(cual)}
        />
    );

    return (
        <div className={`rv-desl${desactivado ? ' rv-desl--quieto' : ''}`}>
            <div className="rv-desl__horas">
                <span className="rv-desl__lado">
                    <span className="rv-desl__k">Desde</span>
                    <span className="rv-desl__valor">
                        <CampoHora valor={formato(desde)} etiqueta={`Desde · ${etiqueta}`} desactivado={desactivado} escribir={conCampo('desde', escribirDesde)} error={erroresCampo.desde} />
                        {desde >= DIA && <MasUno />}
                    </span>
                </span>
                <span className="rv-desl__puntos" aria-hidden="true" />
                <span className="rv-desl__lado rv-desl__lado--fin">
                    <span className="rv-desl__k">Hasta</span>
                    <span className="rv-desl__valor">
                        {hasta > DIA && <MasUno />}
                        <CampoHora valor={formato(hasta)} etiqueta={`Hasta · ${etiqueta}`} desactivado={desactivado} escribir={conCampo('hasta', escribirHasta)} error={erroresCampo.hasta} />
                    </span>
                </span>
            </div>
            <div ref={barra} className="rv-desl__barra" onPointerDown={bajar} onPointerMove={mover} onPointerUp={soltar} onPointerCancel={soltar}>
                <span className="rv-desl__pista" aria-hidden="true" />
                {marcas.map((m) => (
                    <span key={m.valor} className="rv-desl__raya" style={{ left: pos(m.valor) }} aria-hidden="true" />
                ))}
                <span className="rv-desl__lleno" aria-hidden="true" style={{ left: pos(desde), right: `calc(100% - ${pos(hasta)})` }} />
                {bola('desde', desde)}
                {bola('hasta', hasta)}
            </div>
            {marcas.length > 0 && (
                <div className="rv-desl__marcas" aria-hidden="true">
                    {marcas.map((m) => (
                        <span key={m.valor} style={{ left: pos(m.valor) }}>
                            {m.icono !== undefined && <Icono nombre={m.icono} tamano={11} />}
                            {m.texto}
                        </span>
                    ))}
                </div>
            )}
            {errorVisible !== '' && (
                <span role="alert" className="rv-desl__error">
                    <Icono nombre="circle-alert" />
                    <span>{errorVisible}</span>
                </span>
            )}
        </div>
    );
}
